import { useEffect, useRef, useState } from 'preact/hooks';

import { fetchPublicTracker, WidgetApiError, type PublicTrackerResponse } from '../api';
import { strings } from '../messages';
import type { TrackingOptions } from '../types';

interface Props extends TrackingOptions {
  apiUrl: string;
  emit: (event: string, payload: unknown) => void;
}

const FINAL_STATES = new Set(['delivered', 'cancelled', 'failed', 'delivery_failed', 'returned']);
// Polling cadence: 15s → 60s → 300s, capped at 5 min and stopped on a final state.
const POLL_CADENCE_MS = [15_000, 30_000, 60_000, 120_000, 300_000];

export function Tracking({
  apiUrl,
  locale,
  trackingNumber,
  carrierName,
  defaultCarrier,
  autoRefresh = true,
  emit,
}: Props) {
  const t = strings(locale);
  // Pas de slug de secours hardcodé (règle A1) : le site hôte fournit le
  // carrier via ``data-carrier-name`` / ``data-default-carrier`` — le
  // fetch est déjà gardé par ``if (!num || !carrier) return``.
  const [carrier, setCarrier] = useState(carrierName ?? defaultCarrier ?? '');
  const [num, setNum] = useState(trackingNumber);
  const [data, setData] = useState<PublicTrackerResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollAttempt = useRef(0);
  const pollTimer = useRef<number | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  function clearPoll() {
    if (pollTimer.current !== null) {
      window.clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  }

  async function load(initial: boolean) {
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    if (initial) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const resp = await fetchPublicTracker(apiUrl, carrier, num, ac.signal);
      setData(resp);
      const delivered = Boolean(resp.delivered) || resp.status === 'delivered';
      const payload = {
        trackingNumber: num,
        carrierName: carrier,
        status: resp.status,
        delivered,
      };
      emit('tracking-loaded', payload);
      if (autoRefresh && !FINAL_STATES.has(resp.status ?? '')) {
        const wait = POLL_CADENCE_MS[Math.min(pollAttempt.current, POLL_CADENCE_MS.length - 1)];
        pollAttempt.current += 1;
        clearPoll();
        pollTimer.current = window.setTimeout(() => void load(false), wait);
      } else {
        clearPoll();
      }
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') return;
      const apiErr = err as WidgetApiError;
      setError(apiErr.message);
      emit('error', { code: apiErr.code ?? 'tracking_failed', message: apiErr.message });
    } finally {
      if (initial) setLoading(false);
      else setRefreshing(false);
    }
  }

  useEffect(() => {
    pollAttempt.current = 0;
    if (!num || !carrier) return;
    void load(true);
    return () => {
      clearPoll();
      abortRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [num, carrier, apiUrl]);

  // Sync external prop changes.
  useEffect(() => {
    if (trackingNumber && trackingNumber !== num) setNum(trackingNumber);
  }, [trackingNumber]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const c = carrierName ?? defaultCarrier;
    if (c && c !== carrier) setCarrier(c);
  }, [carrierName, defaultCarrier]); // eslint-disable-line react-hooks/exhaustive-deps

  const status = data?.status;
  const delivered = Boolean(data?.delivered) || status === 'delivered';

  return (
    <div class="tsw" role="region" aria-label={t.trackTitle}>
      <div class="tsw-row">
        <span class="tsw-title">{t.trackTitle}</span>
        {status && (
          <span class={badgeClass(status, delivered)} aria-live="polite">
            {delivered ? t.trackDelivered : status}
          </span>
        )}
      </div>
      <div class="tsw-row" style="margin-top:8px;">
        <label class="tsw-sr" htmlFor="tsw-track-num">
          {t.trackPlaceholder}
        </label>
        <input
          id="tsw-track-num"
          class="tsw-input"
          value={num}
          aria-label={t.trackPlaceholder}
          placeholder={t.trackPlaceholder}
          onInput={(e) => setNum((e.target as HTMLInputElement).value)}
        />
        <button
          class="tsw-button"
          type="button"
          onClick={() => {
            pollAttempt.current = 0;
            void load(true);
          }}
        >
          {t.trackButton}
        </button>
      </div>
      <p class="tsw-muted tsw-mono" style="margin:6px 0 0;">{carrier}</p>
      <div role="status" aria-live="polite" aria-atomic="true" style="min-height:1px;">
        {loading && <p class="tsw-muted tsw-loading" style="margin-top:12px;">{t.trackRefreshing}</p>}
        {!loading && refreshing && (
          <p class="tsw-muted tsw-loading" style="margin-top:12px;">{t.trackRefreshing}</p>
        )}
        {error && <p class="tsw-error" style="margin-top:12px;">{t.trackError}</p>}
      </div>
      {!loading && !error && data && (
        <>
          {data.estimated_delivery && (
            <p class="tsw-muted" style="margin:8px 0 0;">
              {t.trackEta}: {data.estimated_delivery}
            </p>
          )}
          {(data.events ?? []).length > 0 ? (
            <ol class="tsw-timeline">
              {(data.events ?? []).map((evt, i) => (
                <li key={i}>
                  <p>{evt.description ?? '—'}</p>
                  <p class="tsw-muted tsw-mono">
                    {[evt.date, evt.time, evt.location].filter(Boolean).join(' · ')}
                  </p>
                </li>
              ))}
            </ol>
          ) : (
            <p class="tsw-muted" style="margin-top:12px;">{t.trackEmpty}</p>
          )}
        </>
      )}
      <p class="tsw-muted" style="margin-top:12px;font-size:11px;">{t.poweredBy}</p>
    </div>
  );
}

function badgeClass(status: string, delivered?: boolean) {
  if (delivered || status === 'delivered') return 'tsw-badge tsw-badge-success';
  if (['failed', 'cancelled', 'delivery_failed', 'returned'].includes(status)) return 'tsw-badge tsw-badge-danger';
  if (['pending', 'in_transit', 'on_hold', 'ready_for_pickup', 'out_for_delivery'].includes(status))
    return 'tsw-badge tsw-badge-warn';
  return 'tsw-badge';
}
